import { ScenarioDetail } from "@/components/domain/scenario-detail"
import type { SuiteHealthSkill } from "@/types/agent_testing"
import type { TestLabScenario, TestRunStatus } from "@/types/test_lab"

type SuiteScenario = SuiteHealthSkill["scenarios"][number]

type Props = {
  skill: SuiteHealthSkill
  scenario: SuiteScenario
}

// Standard scenario testing surface shared by the per-skill Test Lab and the
// agent-wide /testing workspace. /testing gets snake_case suite-health rows;
// ScenarioDetail expects the richer camelCase TestLabScenario shape.
export function SkillTestingPanel({ skill, scenario }: Props) {
  if (skill.skill_id == null) {
    return (
      <ScenarioDetail
        skillId={null}
        scenario={toTestLabScenario(scenario)}
        onFix={() => undefined}
        failurePaneMode="chat"
        runDetailPath={(runId) => `/testing/test_runs/${runId}`}
        discussPayloadPath={(runId) => `/testing/test_runs/${runId}/discuss_payload`}
        scenarioUpdatePath={(scenarioId) => `/testing/scenarios/${scenarioId}`}
      />
    )
  }

  return (
    <ScenarioDetail
      skillId={skill.skill_id}
      scenario={toTestLabScenario(scenario)}
      onFix={() => undefined}
      failurePaneMode="chat"
    />
  )
}

function toTestLabScenario(scenario: SuiteScenario): TestLabScenario {
  const status = normalizeStatus(scenario.latest_status)
  return {
    id: scenario.id,
    key: scenario.key,
    name: scenario.name,
    goal: scenario.goal ?? null,
    persona: scenario.persona ?? null,
    category: scenario.category ?? null,
    scope: scenario.scope ?? null,
    mode: scenario.mode,
    maxTurns: scenario.max_turns ?? null,
    turnsCount: scenario.turns_count ?? 0,
    assertionsCount: scenario.assertions_count ?? 0,
    executable: scenario.executable ?? true,
    skipReason: scenario.skip_reason ?? null,
    allowedToolCalls: scenario.allowed_tool_calls ?? [],
    latestRunId: scenario.latest_run_id,
    latestRunStatus: status,
    latestRunAt: scenario.latest_run_at,
    latestRunSummary: scenario.latest_run_summary ?? null,
    latestRunDurationMs: scenario.latest_run_duration_ms ?? null,
    latestRunCostUsd: scenario.latest_run_cost_usd ?? null,
    latestRunVerdicts: scenario.latest_run_verdicts ?? [],
    endpointMockOverrides: scenario.endpoint_mock_overrides ?? {},
  }
}

function normalizeStatus(status: SuiteScenario["latest_status"]): TestRunStatus | null {
  switch (status) {
    case "queued":
      return "pending"
    case "pending":
    case "running":
    case "passed":
    case "failed":
    case "errored":
    case "skipped":
      return status
    default:
      return null
  }
}
